import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar       from '../components/Navbar';
import Toast        from '../components/Toast';
import StatusBadge  from '../components/StatusBadge';
import { STATUTS, STATUT_ORDER, PAIEMENTS } from '../data/constants';
import { formatDateTime, formatFCFA } from '../utils/helpers';
import { inputStyle, focusInput, blurInput } from '../utils/helpers';

async function parseApiResponse(res) {
  const contentType = res.headers.get('content-type') || '';
  const text = await res.text();
  let json = null;
  const trimmed = (text || '').trim();
  const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[');
  if (contentType.includes('application/json') || looksLikeJson) {
    try {
      json = trimmed ? JSON.parse(trimmed) : null;
    } catch {
      json = null;
    }
  }
  return { ok: res.ok, status: res.status, json, text };
}

function apiUrl(path) {
  const p = String(path || '').replace(/^\/+/, '');
  return `/api/index.php?__path__=${encodeURIComponent(p)}`;
}

export default function SuiviPage() {
  const navigate         = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery]   = useState('');
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const search = async (code) => {
    const q = (code || '').trim();
    if (!q) {
      setError('Veuillez saisir un code');
      return;
    }

    const normalized = q.replace(/\s+/g, '').toUpperCase();
    const m = normalized.match(/^(ZKI)(\d{8})([A-Z0-9]{6})$/);
    const finalCode = m ? `${m[1]}-${m[2]}-${m[3]}` : q;

    setLoading(true);
    setError('');
    setResult(null);
    setSearched(true);
    try {
      const url = `${apiUrl('public/colis')}&code=${encodeURIComponent(finalCode)}`;
      const res = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });
      const out = await parseApiResponse(res);
      if (!out.ok) {
        const msg = out?.json?.message || out?.text || 'Colis introuvable';
        setError(String(msg));
        return;
      }
      setResult(out?.json?.colis || null);
    } catch {
      setError('Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const code = searchParams.get('code');
    if (!code) return;
    setQuery(code);
    search(code);
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    search(query);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      <main style={{ maxWidth: 680, margin: '0 auto', padding: '40px 20px' }}>

        {/* ── Bouton retour ── */}
        <button onClick={() => navigate(-1)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#4F46E5', fontWeight: 600, fontSize: 14,
          marginBottom: 28, display: 'flex', alignItems: 'center', gap: 6,
          fontFamily: 'inherit', padding: 0,
        }}>
          ← Retour
        </button>

        {/* ── Hero ── */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🔍</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>
            Suivre un colis
          </h1>
          <p style={{ fontSize: 15, color: '#64748b' }}>
            Entrez votre numéro de colis ou QR code pour voir l'état de votre livraison.
          </p>
        </div>

        {/* ── Formulaire de recherche ── */}
        <form onSubmit={handleSearch} style={{
          background:   'white',
          borderRadius: 20,
          padding:      28,
          boxShadow:    '0 4px 24px rgba(0,0,0,0.07)',
          marginBottom: 28,
        }}>
          <label style={{
            display: 'block', fontSize: 12, fontWeight: 700,
            color: '#475569', marginBottom: 8,
            textTransform: 'uppercase', letterSpacing: '0.8px',
          }}>
            Numéro de colis ou QR Code *
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              required
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Ex: COL-1234567"
              style={{ ...inputStyle, flex: 1 }}
              onFocus={focusInput}
              onBlur={blurInput}
            />
            <button type="submit" style={{
              padding:      '12px 28px',
              background:   'linear-gradient(135deg, #4F46E5, #8B5CF6)',
              color:        'white',
              border:       'none',
              borderRadius: 12,
              fontWeight:   700,
              fontSize:     14,
              cursor:       'pointer',
              fontFamily:   'inherit',
              whiteSpace:   'nowrap',
              boxShadow:    '0 4px 12px rgba(79,70,229,0.3)',
              transition:   'opacity 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Rechercher
            </button>
          </div>

          {/* Astuce */}
          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>
            💡 Astuce : le numéro commence par <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>ZKI-</code> et se trouve sur votre reçu.
          </p>
        </form>

        {error && (
          <div style={{
            background: 'white',
            borderRadius: 20,
            padding: 18,
            boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
            marginBottom: 18,
            color: '#B91C1C',
            fontWeight: 800,
          }}>
            {error}
          </div>
        )}

        {/* ── Résultat : non trouvé ── */}
        {searched && !result && !error && (
          <div style={{
            background:   'white',
            borderRadius: 20,
            padding:      48,
            textAlign:    'center',
            boxShadow:    '0 4px 24px rgba(0,0,0,0.07)',
            animation:    'fadeIn 0.3s ease-out',
          }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>😕</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
              Colis introuvable
            </div>
            <div style={{ fontSize: 14, color: '#94a3b8' }}>
              Le numéro « <strong>{query}</strong> » ne correspond à aucun colis dans notre système.
            </div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 8 }}>
              Vérifiez le numéro sur votre reçu ou contactez la centrale.
            </div>
          </div>
        )}

        {/* ── Résultat : trouvé ── */}
        {result && <ColisTracking colis={result} />}
      </main>

      <Toast />
    </div>
  );
}

// ─── Composant tracking détaillé ────────────────────────────────────────────

function ColisTracking({ colis }) {
  const currentIdx = STATUT_ORDER.indexOf(colis.statut);
  const paiement   = PAIEMENTS[colis.paiement] || { label: colis.paiement, icon: '💳' };

  const motifEchec = colis?.motif_echec || colis?.motif || '';

  const steps = [
    { key: 'depot',    label: 'Déposé à la centrale', detail: 'Colis enregistré' },
    { key: 'affecte',  label: 'Livreur assigné',       detail: colis.livreur ? `🛵 ${colis.livreur}` : '' },
    { key: 'en_route', label: 'En route',              detail: 'Le livreur est en chemin' },
    { key: 'livre',    label: 'Livré !',               detail: 'Livraison confirmée' },
  ];

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-out' }}>

      {/* Header colis */}
      <div style={{
        background:   'white',
        borderRadius: 20,
        padding:      28,
        boxShadow:    '0 4px 24px rgba(0,0,0,0.07)',
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#4F46E5', fontFamily: 'monospace', letterSpacing: 1 }}>
              {colis.id}
            </div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 3 }}>
              {formatDateTime(colis.date)}{colis.nom ? ` · ${colis.nom}` : ''}{colis.zone ? ` · ${colis.zone}` : ''}
            </div>
          </div>
          <StatusBadge statut={colis.statut} />
        </div>

        {colis.statut === 'echec' && motifEchec && (
          <div style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 14,
            padding: '12px 14px',
            color: '#991B1B',
            fontWeight: 800,
            marginBottom: 14,
          }}>
            Motif de l'échec : {motifEchec}
          </div>
        )}

        {/* Timeline */}
        <div>
          {steps.map((step, i) => {
            const done   = i <= currentIdx && colis.statut !== 'echec';
            const active = i === currentIdx && colis.statut !== 'echec';
            const last   = i === steps.length - 1;

            return (
              <div key={step.key} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                {/* Icône + trait */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{
                    width:        40,
                    height:       40,
                    borderRadius: '50%',
                    background:   done
                      ? 'linear-gradient(135deg, #4F46E5, #8B5CF6)'
                      : '#f1f5f9',
                    display:      'flex',
                    alignItems:   'center',
                    justifyContent: 'center',
                    fontSize:     18,
                    boxShadow:    active ? '0 0 0 5px rgba(79,70,229,0.18)' : 'none',
                    transition:   'all 0.3s',
                  }}>
                    {done
                      ? STATUTS[step.key]?.icon || '✓'
                      : <span style={{ fontSize: 12, color: '#cbd5e1' }}>○</span>
                    }
                  </div>
                  {!last && (
                    <div style={{
                      width:      2,
                      height:     30,
                      background: done && i < currentIdx ? '#4F46E5' : '#e2e8f0',
                      margin:     '4px 0',
                      transition: 'background 0.3s',
                    }} />
                  )}
                </div>

                {/* Texte */}
                <div style={{ paddingTop: 8, paddingBottom: last ? 0 : 8 }}>
                  <div style={{
                    fontSize:   14,
                    fontWeight: done ? 700 : 500,
                    color:      done ? '#1e293b' : '#94a3b8',
                  }}>
                    {step.label}
                  </div>
                  {step.detail && done && (
                    <div style={{ fontSize: 12, color: active ? '#4F46E5' : '#64748b', marginTop: 2 }}>
                      {step.detail}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Cas d'échec */}
          {colis.statut === 'echec' && (
            <div style={{
              marginTop:    12,
              background:   '#FEE2E2',
              borderRadius: 12,
              padding:      '12px 16px',
              display:      'flex',
              alignItems:   'center',
              gap:          10,
            }}>
              <span style={{ fontSize: 20 }}>❌</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#DC2626' }}>Échec de livraison</div>
                <div style={{ fontSize: 12, color: '#EF4444' }}>Contactez la centrale pour reprogrammer.</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Détails du colis */}
      <div style={{
        background:   'white',
        borderRadius: 20,
        padding:      28,
        boxShadow:    '0 4px 24px rgba(0,0,0,0.07)',
      }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', marginBottom: 20 }}>
          Détails du colis
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <DetailItem label="Expéditeur">{colis.expediteur}</DetailItem>
          <DetailItem label="Téléphone">{colis.tel}</DetailItem>
          <DetailItem label="Destinataire">{colis.destinataire}</DetailItem>
          <DetailItem label="Zone">{colis.zone}</DetailItem>
          <DetailItem label="Adresse" full>{colis.adresse}</DetailItem>
          <DetailItem label="Poids">{colis.poids} kg</DetailItem>
          <DetailItem label="Montant">{formatFCFA(colis.montant)}</DetailItem>
          <DetailItem label="Paiement">{paiement.icon} {paiement.label}</DetailItem>
          {colis.livreur && (
            <DetailItem label="Livreur" full>🛵 {colis.livreur}</DetailItem>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailItem({ label, children, full }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : undefined }}>
      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{children}</div>
    </div>
  );
}
